import { AntennasGame, Antenna } from './AntennasGame.js';
import { AntennasRenderer } from './AntennasRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position } from '../../core/GridHitDetector.js';
import { Direction, positionsEqual } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, TraceStep, TraceDirResult } from '../../input/KeyboardController.js';

/** One-cell step per direction, for vetting a trace move before the cursor takes it. */
const STEP: Record<Direction, { dr: number; dc: number }> = {
    N: { dr: -1, dc: 0 }, S: { dr: 1, dc: 0 }, E: { dr: 0, dc: 1 }, W: { dr: 0, dc: -1 },
};

export class AntennasInput extends BaseInput<AntennasGame, AntennasRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: AntennasGame,
        renderer: AntennasRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);

        // If currently building an antenna, cancel the build
        if (this.game.getCurrentPath().length > 0) {
            this.game.cancelAntenna();
            this.onUpdate();
            return;
        }

        const hit = this.detector.detectCell(x, y);
        if (!hit) return;

        const antenna = this.game.getAntennaAtCell({ row: hit.row, col: hit.col });
        if (antenna) {
            this.game.removeAntenna(antenna.id);
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        const hit = this.detector.detectCell(x, y);
        if (!hit) return;

        if (this.gateMode && isVortexGame(this.game)) {
            if (this.game.toggleVortexGate(hit)) {
                this.onUpdate();
            }
            return;
        }

        const cell = { row: hit.row, col: hit.col };
        if (this.game.handleClick(cell)) {
            this.onUpdate();
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // A tap mirrors the click (start / extend / finish / cancel / cycle / remove).
    // The trace is where the game speeds up: hold space and steer to draw the
    // antenna body, release to drop the emitter. Press space again straight away
    // and the arrows aim it (see traceDirPress) — draw, release, aim, release.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        return this.game.handleClick(pos);
    }

    /** Mirrors the right-click: abandon the build in progress, else delete the
     *  antenna under the cursor. */
    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (this.game.getCurrentPath().length > 0) {
            this.game.cancelAntenna();
            return true;
        }
        const antenna = this.game.getAntennaAtCell(pos);
        if (!antenna) return false;
        return this.game.removeAntenna(antenna.id);
    }

    traceStart(pos: Position): void {
        if (this.gateMode) return;
        this.beginBuild(pos);
    }

    /** Each step draws one more cell of the body — or, stepping back onto the cell
     *  before the end, retracts it. */
    traceStep({ from, to }: TraceStep): boolean {
        if (this.gateMode) return false;
        // Aim mode and blocked presses count as trace steps, so `traceStart` may
        // never have fired: if the build isn't anchored under the cursor, start it
        // here (from the emitter we just left, or a fresh cell).
        if (!this.isBuildingAt(from) && !this.beginBuild(from)) return false;

        const path = this.game.getCurrentPath();
        if (path.length >= 2 && positionsEqual(path[path.length - 2], to)) {
            return this.game.retractBuild();
        }
        return this.game.extendAntenna(to);
    }

    /**
     * Vet a direction press mid-gesture. On a finished emitter the arrows aim it
     * instead of moving (the one direction back along the body falls through, to
     * reopen the antenna). Otherwise the cursor is pinned to the antenna being
     * drawn: a step onto a digit, another antenna, or back over our own body is
     * refused rather than letting the cursor wander off the build.
     */
    traceDirPress(pos: Position, dir: Direction): TraceDirResult {
        if (this.gateMode) return 'blocked';

        const antenna = this.aimTarget(pos);
        if (antenna) {
            if (!this.game.getValidSignalDirections(antenna).includes(dir)) {
                return 'move'; // back along the body: reopen it as a build
            }
            // Claim on the direction being *aimable*, not on it having changed —
            // re-pressing the current heading must still hold the cursor in aim mode.
            this.game.setSignalDirection(antenna.id, dir);
            return 'handled';
        }

        const d = STEP[dir];
        return this.canTraceInto(pos, { row: pos.row + d.dr, col: pos.col + d.dc })
            ? 'move' : 'blocked';
    }

    /** Whether a trace step from `from` into `to` may be drawn. */
    private canTraceInto(from: Position, to: Position): boolean {
        const path = this.game.getCurrentPath();

        if (this.isBuildingAt(from)) {
            // Stepping back onto the cell before the end retracts — always allowed.
            if (path.length >= 2 && positionsEqual(path[path.length - 2], to)) return true;
            // Anywhere else on our own body would self-overlap.
            if (path.some(p => positionsEqual(p, to))) return false;
            return this.game.canBuildOn(to);
        }

        // The build is about to (re)start at `from`. If that reopens a finished
        // antenna, its own cells are freed by the reopen, so they don't block.
        const reopened = this.game.getAntennaAtCell(from);
        if (reopened?.path.some(p => positionsEqual(p, to))) return true;
        return this.game.canBuildOn(to);
    }

    /** Whether the pending build is anchored under the cursor — i.e. this trace is
     *  the one that drew it. */
    private isBuildingAt(pos: Position): boolean {
        const path = this.game.getCurrentPath();
        return path.length > 0 && positionsEqual(path[path.length - 1], pos);
    }

    /** Releasing drops the emitter, finishing the antenna. No-op in aim mode
     *  (nothing is being built) or when the body never got past one cell. */
    traceEnd(): boolean {
        if (this.gateMode) return false;
        return this.game.finishAntenna();
    }


    /** The finished antenna the cursor is aiming: nothing pending, cursor on its emitter. */
    private aimTarget(pos: Position): Antenna | null {
        if (this.game.getCurrentPath().length > 0) return null;
        const antenna = this.game.getAntennaAtCell(pos);
        if (!antenna) return null;
        return positionsEqual(this.game.getSignalEnd(antenna), pos) ? antenna : null;
    }

    /**
     * Begin drawing at `pos`, whatever is already there:
     *  - on the pending build's own body → cut it back to here and carry on
     *  - on a finished antenna → reopen it, keeping its receiver side
     *  - anywhere else → abandon any pending build and anchor a fresh receiver
     */
    private beginBuild(pos: Position): boolean {
        if (this.game.truncateBuild(pos)) return true;
        this.game.cancelAntenna();
        return this.game.getAntennaAtCell(pos)
            ? this.game.resumeAntenna(pos)
            : this.game.startAntenna(pos);
    }

    /** `X` goes to the first digit — where a solution has to start. */
    cursorOrigin(): Position | null {
        const grid = this.game.getGrid();
        return grid.getDigitPosition(grid.minDigit);
    }
}
